import React, { useState, useEffect } from "react";
import Toolbar from "@material-ui/core/Toolbar";
import Grid from "@material-ui/core/Grid";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import { FootballContextUtils } from "@prestosports/prestostats-core/Common";
import Box from "@material-ui/core/Box";
import { useFootballContext } from "../../../../state/football-play/context";
import EventTimePicker from "../EventTimePicker";
import FormMessages from "../../../errors/components/FormMessages";
import GamePeriod from "../GamePeriod";
import { useGameContextContext } from "../../../../state/game-context/context";
import SubmitCancelListener from "../listeners/SubmitCancelListener";

const TimeAndPeriodSelector = ({
	disableKeyListener,
	selected,
	onSelected,
	hideCancel,
	editingPlay = false,
	isInsertingPlay = false
}) => {
	const { startOver } = useFootballContext();
	const { state: contextState } = useGameContextContext();
	const currentPeriod = contextState.period();
	const gameConfig = contextState.gameConfig();

	const [errors, setErrors] = useState({});
	const [state, setState] = useState({
		selectedPeriod: selected.period,
		suggestedSeconds: selected.milliseconds / 1000,
		totalSeconds: selected.milliseconds / 1000,
		isTimeDisabled: currentPeriod !== selected.period
	});

	const onChangePeriod = (value) => {
		const periodChanged = currentPeriod !== value;

		const newState = { ...state };
		newState.selectedPeriod = value;
		newState.isTimeDisabled = periodChanged;

		if (periodChanged) {
			// The new period's own length: a timed overtime period has its own
			// (OT Period Length, PSTATS-1268).
			const seconds = FootballContextUtils.getPeriodStartSeconds(
				FootballContextUtils.getPeriodMinutes(gameConfig, value)
			);
			newState.suggestedSeconds = seconds;
			newState.totalSeconds = seconds;
		} else {
			newState.suggestedSeconds = selected.milliseconds / 1000;
		}
		setState(newState);
	};

	const confirm = () => {
		onSelected({
			milliseconds: state.totalSeconds * 1000,
			period: state.selectedPeriod
		});
	};

	const hasErrors = () => {
		return !!Object.keys(errors).length;
	};

	useEffect(() => {
		if (!hasErrors() && editingPlay) {
			confirm();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.totalSeconds, state.selectedPeriod, editingPlay]);

	return (
		<>
			{hasErrors() && (
				<Box pt={2} pb={2}>
					<FormMessages messages={errors} />
				</Box>
			)}
			<Box display="flex" justifyContent="center">
				<EventTimePicker
					period={state.selectedPeriod}
					seconds={state.suggestedSeconds}
					onChange={(seconds) => {
						setState({ ...state, totalSeconds: seconds });
					}}
					hideErrors
					onValidate={(isValid, _errors) => {
						setErrors(_errors);
					}}
					disabled={state.isTimeDisabled}
				/>
			</Box>
			<Box display="flex" justifyContent="center">
				<GamePeriod
					onChange={onChangePeriod}
					disabled={editingPlay || isInsertingPlay}
					selectedPeriod={selected.period}
				/>
			</Box>
			{!editingPlay && (
				<Toolbar disableGutters>
					<Grid
						container
						direction="row"
						justifyContent="flex-end"
						alignItems="center"
						spacing={1}
					>
						{!hideCancel && (
							<Grid item>
								<SecondaryButton
									onClick={startOver}
									variant="contained"
									label="Cancel"
								/>
							</Grid>
						)}
						<Grid item>
							<PrimaryButton
								id="button-save"
								onClick={confirm}
								label="Save"
								disabled={hasErrors()}
							/>
						</Grid>
					</Grid>
				</Toolbar>
			)}
			{!disableKeyListener && (
				<SubmitCancelListener
					submitDisabled={hasErrors()}
					onSubmit={confirm}
					onCancel={startOver}
				/>
			)}
		</>
	);
};

export default TimeAndPeriodSelector;
